// src/pages/Wallet/modals/NetworkModal.js
import React, { useState, useContext } from 'react';
import { NetworkContext } from '../../../contexts/NetworkContext';
import { getChainList } from '../../../chains';
import { useTranslation } from 'react-i18next';
import { useNetworkManagement } from '../hooks/useNetworkManagement';
import chainLogos, { getChainLogo } from '../chainLogos';
import './NetworkModal.css';

const NetworkModal = ({ isOpen, onClose, onSwitchNetwork }) => {
  const { t } = useTranslation();
  const { currentChain } = useContext(NetworkContext);
  const { hiddenNetworks, toggleNetworkVisibility, restoreAllNetworks } = useNetworkManagement();
  
  // State for active tab
  const [activeTab, setActiveTab] = useState('available');
  
  // Dynamically generate network list from supported chains
  const allNetworks = getChainList().map(chain => ({
    label: chain.name,
    value: chain.key,
    chainId: chain.chainId
  }));
  
  // Filter networks based on active tab
  const availableNetworks = allNetworks.filter(net => !hiddenNetworks.includes(net.value));
  const hiddenNetworkList = allNetworks.filter(net => hiddenNetworks.includes(net.value));
  
  // Current networks list based on active tab
  const displayedNetworks = activeTab === 'available' ? availableNetworks : hiddenNetworkList;
  
  if (!isOpen) return null;

  const handleNetworkClick = (networkValue) => {
    onSwitchNetwork(networkValue);
  };
  
  const handleToggleVisibility = (e, networkValue) => {
    e.stopPropagation();
    
    // Don't allow hiding the current network
    if (currentChain?.key === networkValue) {
      alert(t('wallet.cannotHideCurrentNetwork', "You cannot hide the currently selected network"));
      return;
    }
    
    // Don't allow hiding if it would hide all networks
    if (activeTab === 'available' && availableNetworks.length <= 1) {
      alert(t('wallet.cannotHideAllNetworks', "You must keep at least one network visible"));
      return;
    }
    
    toggleNetworkVisibility(networkValue);
  };
  
  // Status dot style with color based on hidden status
  const getStatusDotStyle = (isHidden) => ({
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    marginRight: '8px',
    backgroundColor: isHidden ? '#ef4444' : '#10b981', // Red for hidden, green for available
    boxShadow: isHidden 
      ? '0 0 4px rgba(239, 68, 68, 0.6)' 
      : '0 0 4px rgba(16, 185, 129, 0.6)'
  });

  return (
    <div className="network-modal-overlay" onClick={onClose}>
      <div className="network-modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>{t('wallet.selectNetwork', 'Select Network')}</h2>
        
        {/* Tab navigation */}
        <div className="network-modal-tabs">
          <button 
            className={`network-tab-btn ${activeTab === 'available' ? 'active' : ''}`}
            onClick={() => setActiveTab('available')}
          >
            {t('wallet.availableNetworks', 'Available')}
          </button>
          <button 
            className={`network-tab-btn ${activeTab === 'hidden' ? 'active' : ''}`}
            onClick={() => setActiveTab('hidden')}
          >
            {t('wallet.hiddenNetworks', 'Hidden')}
            {hiddenNetworkList.length > 0 && ` (${hiddenNetworkList.length})`}
          </button>
        </div>

        {/* Network List */}
        <div className="network-list">
          {displayedNetworks.length > 0 ? (
            displayedNetworks.map((net) => (
              <div
                key={net.value}
                className={`network-item ${currentChain?.key === net.value ? 'active' : ''}`}
                onClick={() => handleNetworkClick(net.value)}
                style={{ display: 'flex', alignItems: 'center' }}
              >
                {/* Logo */}
                <div style={{ 
                  width: '28px', 
                  height: '28px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  marginRight: '12px' 
                }}>
                  <img 
                    src={getChainLogo(net.value)} 
                    alt={net.label} 
                    style={{ width: '24px', height: '24px', borderRadius: '50%' }}
                  />
                </div>
                
                {/* Network name with status dot */}
                <div style={{ flex: 1, textAlign: 'left' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    {/* Using inline styles to avoid CSS class conflicts */}
                    <div style={getStatusDotStyle(hiddenNetworks.includes(net.value))}></div>
                    {net.label}
                  </div>
                </div>
                
                {/* Toggle switch */}
                <label 
                  style={{ 
                    position: 'relative',
                    display: 'inline-block',
                    width: '48px',
                    height: '24px',
                    marginLeft: '10px'
                  }} 
                  onClick={(e) => e.stopPropagation()}
                >
                  <input 
                    type="checkbox" 
                    checked={!hiddenNetworks.includes(net.value)} 
                    onChange={(e) => handleToggleVisibility(e, net.value)} 
                    style={{ opacity: 0, width: 0, height: 0 }}
                  />
                  <span className="toggle-slider-network"></span>
                </label>
              </div>
            ))
          ) : (
            <div className="no-networks-message">
              {activeTab === 'available' 
                ? t('wallet.noAvailableNetworks', 'All networks are hidden. Restore networks from the Hidden tab.') 
                : t('wallet.noHiddenNetworks', 'No hidden networks')}
            </div>
          )}
        </div>
        
        {/* Restore networks button (only shown in hidden tab) */}
        {activeTab === 'hidden' && hiddenNetworkList.length > 0 && (
          <button className="restore-all-networks" onClick={restoreAllNetworks}>
            {t('wallet.restoreAllNetworks', 'Restore All Networks')}
          </button>
        )}

        <button className="close-button-network" onClick={onClose}>
          {t('actions.close', 'Close')}
        </button>
      </div>
    </div>
  );
};

export default NetworkModal;